import React, { useState } from "react";
import { SubNav, FileDropzone } from "../gc/ui";
import CodeEditorPanel from "./CodeEditorPanel";
import VisualScriptingPanel from "./VisualScriptingPanel";
import { CODE_TEMPLATES, languageFromPath, readAsText, downloadText } from "../../lib/gamecraft";
import ModuleShell, { EmptyCraft, RelatedStrip } from "../gc/ModuleShell";
import { studioHealth, studioCast } from "../../lib/studioHealth";
import { toast } from "sonner";

const SNIPPETS = [
  { id: "pickup", label: "Al recoger", code: `window.GC_ON_PICKUP = function (item) {\n  console.log("pickup", item && item.name);\n};\n` },
  { id: "attack", label: "Al atacar", code: `window.GC_ON_ATTACK = function (hit) {\n  if (hit && hit.killed) console.log("down", hit.name);\n};\n` },
  { id: "roll", label: "Al rodar", code: `window.GC_ON_ROLL = function () {\n  console.log("i-frames");\n};\n` },
  { id: "use", label: "Al usar slot", code: `window.GC_ON_USE = function (item) {\n  console.log("use", item);\n};\n` },
];

export default function CodeHub({ project, onChange, onJump }) {
  const [sub, setSub] = useState("editor");
  const files = project.files || [];
  const lang = project.config?.language || "javascript";
  const score = studioHealth(project).modules.find((m) => m.id === "code")?.score ?? 0;
  const jsFiles = files.filter((f) => /\.js$/i.test(f.path || ""));
  const cast = studioCast(project);

  const upload = async (list) => {
    const next = [...files];
    for (const f of list) {
      const content = await readAsText(f);
      next.push({ id: crypto.randomUUID(), path: f.name, language: languageFromPath(f.name), content });
    }
    onChange({ files: next });
    toast.success("Archivo importado");
  };

  const applyTemplates = () => {
    const pack = CODE_TEMPLATES[lang] || CODE_TEMPLATES.javascript;
    const next = [...files];
    Object.entries(pack).forEach(([path, content]) => {
      if (!next.some((f) => f.path === path)) {
        next.push({ id: crypto.randomUUID(), path, language: languageFromPath(path), content });
      }
    });
    onChange({ files: next });
    toast.success("Plantillas añadidas");
  };

  const insertSnippet = (snip) => {
    const path = "src/mechanics.js";
    const existing = files.find((f) => f.path === path);
    const next = existing
      ? files.map((f) => (f.path === path ? { ...f, content: `${f.content || ""}\n${snip.code}` } : f))
      : [...files, { id: crypto.randomUUID(), path, language: "javascript", content: snip.code }];
    onChange({ files: next });
    setSub("editor");
    toast.success(`${snip.label} en mechanics.js — el iframe lo ejecuta en Run`);
  };

  const downloadAll = () => {
    files.forEach((f) => downloadText(f.content || "", f.path.split("/").pop()));
  };

  return (
    <ModuleShell
      title="Código"
      subtitle="El playtest solo corre JavaScript en el iframe. Python/Godot/C# son plantillas de export."
      beginner="No hace falta programar para el primer loop. Si escribes, usa mechanics.js y los hooks GC_ON_*."
      expert="Monaco · Ctrl+S guarda vía auto-save del IDE. Snippets se concatenan, no pisan."
      score={score}
      related={(
        <RelatedStrip items={files.filter((f) => cast.linkedFileIds.has(f.id) || /mechanics\.js$/i.test(f.path || "")).slice(0, 4).map((f) => ({
          id: f.id,
          label: f.path,
          onClick: () => { setSub("editor"); onJump?.("code", { fileId: f.id }); },
        }))} />
      )}
      actions={(
        <>
          <SubNav
            items={[
              { id: "editor", label: "Editor" },
              { id: "snippets", label: "Snippets" },
              { id: "templates", label: "Plantillas" },
              { id: "visual", label: "Visual" },
            ]}
            value={sub}
            onChange={setSub}
          />
          <FileDropzone accept=".js,.ts,.py,.gd,.cs,.lua,.json,.md,.html,.css,.txt" multiple onFiles={upload} label="Subir código" />
          <button className="gc-btn gc-btn-ghost h-10 px-3 text-xs" onClick={downloadAll}>Descargar</button>
        </>
      )}
    >
      <div className="h-full min-h-[420px] flex flex-col">
        {sub === "editor" && (
          jsFiles.length === 0 && !files.length ? (
            <EmptyCraft
              title="Aún no hay JS de playtest"
              body="Inserta plantillas JavaScript o un snippet. El motor ignora Python."
              cta="Insertar plantillas JS"
              onCta={() => { onChange({ config: { ...(project.config || {}), language: "javascript" } }); applyTemplates(); }}
              tips={["GC_ON_PICKUP / ATTACK / ROLL / USE", "Inventario: window.GC_INV_MAX", "Tras editar, Run de nuevo"]}
            />
          ) : (
            <div className="h-full gc-tool" style={{ minHeight: 520 }}><CodeEditorPanel project={project} onChange={onChange} /></div>
          )
        )}
        {sub === "visual" && <VisualScriptingPanel project={project} onChange={onChange} />}
        {sub === "snippets" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SNIPPETS.map((s) => (
              <button key={s.id} type="button" className="gc-card p-4 text-left hover:brightness-105" onClick={() => insertSnippet(s)}>
                <div className="font-semibold">{s.label}</div>
                <pre className="text-[11px] mt-2 overflow-auto" style={{ color: "var(--gc-muted)" }}>{s.code}</pre>
              </button>
            ))}
          </div>
        )}
        {sub === "templates" && (
          <div className="space-y-3">
            <p className="text-sm" style={{ color: "var(--gc-muted)" }}>
              Activo: <b>{lang}</b>. El iframe Phaser solo ejecuta .js. Cambia el lenguaje en Proyecto → Configuración si exportas a Godot/Unity.
            </p>
            <button className="gc-btn gc-btn-primary h-10 px-4" onClick={applyTemplates}>Insertar plantillas {lang}</button>
            <pre className="gc-tool p-4 text-xs overflow-auto max-h-[50vh]">
              {Object.entries(CODE_TEMPLATES[lang] || CODE_TEMPLATES.javascript).map(([k, v]) => `// ${k}\n${v}\n`).join("\n")}
            </pre>
          </div>
        )}
      </div>
    </ModuleShell>
  );
}
